import React, {useMemo} from "react";
import {useFormState} from "../../../hooks/useFormState";
import Button from "../../../ui/button";
import classes from "./MoveChallengeModal.module.css";
import {useTranslation} from "react-i18next";
import Select from "../../../ui/select";
import {Project, Challenge, ChallengeInput} from "../../../schema";
import {gql} from "@apollo/client";

type contentProps = Omit<React.ComponentProps<"form">, "className" | "method" | "onSubmit"> & {
  initialId?: Project["id"] | null,
  challenge: Pick<Challenge, "id" | "name">,
  projects?: Pick<Project, "id" | "name">[],
  onMove: (data: Pick<ChallengeInput, "projectId">) => void,
}

function MoveChallengeModalHeader() {
  const {t} = useTranslation();

  return <span>{t("components.MoveChallengeModal.title")}</span>
}

 function MoveChallengeModalContent({initialId, challenge, projects, onMove, ...props}: contentProps) {
  const {t} = useTranslation();

  const formState = useFormState<{
    projectId: string | "no"
  }>({
    initialValues: {
      projectId: initialId ?? "no"
    },

    preventDefault: true,
    onSubmit: (data) => {
      onMove({
        projectId: (data.projectId === "no") ? null : data.projectId
      })
    }
  });
  const targetProjectId = formState.values.projectId
  const idChanged = targetProjectId !== (initialId ?? "no")

  const initialProject = useMemo(() => projects?.find(({id}) => id === initialId), [projects, initialId])
  const targetProject = useMemo(() => projects?.find(({id}) => id === targetProjectId), [projects, targetProjectId])

  const [moveWarning, moveHelpText] = useMemo<[string | undefined, string | undefined]>(() => {
    if (!idChanged) {
      return [undefined, undefined]
    }

    const names = {
      challengeName: challenge.name,
      initialProjectName: initialProject?.name,
      targetProjectName: targetProject?.name
    }

    if (initialProject && targetProject) {
      // Move challenge between two projects
      return [
        t("components.MoveChallengeModal.loseAssignWarning.project", names),
        t("components.MoveChallengeModal.autoAssign", names)
      ]
    }
    if (!initialProject && targetProject) {
      // Move challenge into project
      return [
        t("components.MoveChallengeModal.loseAssignWarning.challenge", names),
        t("components.MoveChallengeModal.autoAssign", names)
      ]
    }
    if (initialProject && !targetProject) {
      // Move challenge out of project
      return [
        t("components.MoveChallengeModal.loseAssignWarning.project", names),
        undefined
      ]
    }
    return [undefined, undefined]
  }, [t, idChanged, challenge, initialProject, targetProject])

  return (
    <form className={classes.root} method="post" onSubmit={formState.submitHandler} {...props}>
      <p className={classes.helpText}>{t("components.MoveChallengeModal.helpText")}</p>
      <Select
        className={classes.input}
        name="projectId"
        value={formState.values.projectId}
        onChange={formState.changeHandler}
      >
        <option value="no">
          {t("components.MoveChallengeModal.no")}
        </option>
        {projects && projects.map((item) => (
          <option key={item.id} value={item.id} translate="no">
            {item.name}
          </option>
        ))}
      </Select>

      {moveHelpText && (<p className={classes.helpText}>{moveHelpText}</p>)}
      {moveWarning && (<p className={classes.warning}>{moveWarning}</p>)}

      <Button type="submit"
        className={classes.submitButton}
        color={idChanged ? "success" : "secondary"}
        disabled={!idChanged}
      >
        {t("components.MoveChallengeModal.submit")}
      </Button>
    </form>
  )
}

const MoveChallengeModal = {
  Header: MoveChallengeModalHeader,
  Content: MoveChallengeModalContent,
  fragments: {
    root: gql`
      fragment MoveChallengeModal on Challenge {
        id
        name
      }
    `,
    projects: gql`
      fragment MoveChallengeModalProjects on Project {
        id
        name
      }
    `
  }
}

export default MoveChallengeModal;
